<template>
  <div class="sidebar-wrapper">
    <!-- Mobile Menu Button -->
    <button class="mobile-menu-btn" @click="toggleMobileSidebar">
      <vue-feather :type="isMobileOpen ? 'x' : 'menu'" size="24" />
    </button>

    <!-- Sidebar Overlay for Mobile -->
    <div class="sidebar-overlay" :class="{ active: isMobileOpen }" @click="closeMobileSidebar"></div>

    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'mobile-open': isMobileOpen }">
      <div class="sidebar-inner">
        <!-- Logo Section -->
        <div class="sidebar-logo">
          <router-link to="/app/dashboard" class="logo-link">
            <div class="logo-icon-wrapper">
              <img src="@/assets/img/testpal-logo-udai.png" alt="TestPal" class="logo-img" />
            </div>
          </router-link>
        </div>

        <!-- Navigation Menu -->
        <nav class="sidebar-nav">
          <div class="active-indicator" :style="indicatorStyle" v-show="activeIndex >= 0"></div>
          
          <ul class="nav-list" ref="navList">
            <li :class="{ active: isActive('/app/dashboard') }">
              <router-link to="/app/dashboard" class="nav-link">
                <span class="nav-icon"><vue-feather type="home" size="20" /></span>
                <span class="nav-text">Home</span>
              </router-link>
            </li>

            <li :class="{ active: isExamsActive }">
              <router-link to="/app/exams/list" class="nav-link">
                <span class="nav-icon"><vue-feather type="file-text" size="20" /></span>
                <span class="nav-text">Exams</span>
              </router-link>
            </li>

            <li :class="{ active: isActive('/app/questions') }">
              <router-link to="/app/questions/list" class="nav-link">
                <span class="nav-icon"><vue-feather type="help-circle" size="20" /></span>
                <span class="nav-text">Questions</span>
              </router-link>
            </li>

            <li :class="{ active: isActive('/app/notes') }">
              <router-link to="/app/notes/list" class="nav-link">
                <span class="nav-icon"><vue-feather type="book-open" size="20" /></span>
                <span class="nav-text">Notes</span>
              </router-link>
            </li>

            <li :class="{ active: isActive('/app/flashcards') }">
              <router-link to="/app/flashcards/list" class="nav-link">
                <span class="nav-icon"><vue-feather type="layers" size="20" /></span>
                <span class="nav-text">Flashcards</span>
              </router-link>
            </li>

            <li :class="{ active: isActive('/app/bulletins') }">
              <router-link to="/app/bulletins/list" class="nav-link">
                <span class="nav-icon"><vue-feather type="radio" size="20" /></span>
                <span class="nav-text">Bulletins</span>
              </router-link>
            </li>

            <li :class="{ active: isActive('/app/pattern-insights') }">
              <router-link to="/app/pattern-insights/list" class="nav-link">
                <span class="nav-icon"><vue-feather type="trending-up" size="20" /></span>
                <span class="nav-text">Patterns</span>
              </router-link>
            </li>

            <li :class="{ active: isActive('/app/reports') }">
              <router-link to="/app/reports" class="nav-link">
                <span class="nav-icon"><vue-feather type="bar-chart-2" size="20" /></span>
                <span class="nav-text">Reports</span>
              </router-link>
            </li>

            <li class="nav-divider"></li>

            <li :class="{ active: isActive('/app/notifications') }">
              <router-link to="/app/notifications" class="nav-link">
                <span class="nav-icon-badge">
                  <vue-feather type="bell" size="20" />
                  <span class="badge" v-if="notificationCount">{{ notificationCount }}</span>
                </span>
                <span class="nav-text">Alerts</span>
              </router-link>
            </li>

            <li :class="{ active: isActive('/app/users') }" v-if="isAdmin">
              <router-link to="/app/users/list" class="nav-link">
                <span class="nav-icon"><vue-feather type="users" size="20" /></span>
                <span class="nav-text">Users</span>
              </router-link>
            </li>

            <li :class="{ active: isActive('/app/approvals') }">
              <router-link to="/app/approvals" class="nav-link">
                <span class="nav-icon-badge">
                  <vue-feather type="check-square" size="20" />
                  <span class="badge" v-if="pendingApprovals">{{ pendingApprovals }}</span>
                </span>
                <span class="nav-text">Approvals</span>
              </router-link>
            </li>

            <li class="nav-divider"></li>

            <li :class="{ active: isActive('/app/settings') }">
              <router-link to="/app/settings/profile" class="nav-link">
                <span class="nav-icon"><vue-feather type="settings" size="20" /></span>
                <span class="nav-text">Settings</span>
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- Bottom Section -->
        <div class="sidebar-bottom">
          <button class="bottom-btn" @click="openSearch" title="Search (⌘K)">
            <vue-feather type="search" size="18" />
          </button>

          <button class="user-avatar-btn" ref="userAvatarBtn" @click="toggleUserMenu">
            <img :src="userAvatar" :alt="user?.fullName || 'User'" />
            <span class="online-dot"></span>
          </button>
        </div>
      </div>
    </aside>

    <!-- User Menu (Outside sidebar to avoid overflow clipping) -->
    <Teleport to="body">
      <Transition name="user-popup">
        <div v-if="showUserMenu" class="user-popup-overlay" @click="showUserMenu = false">
          <div class="user-popup" :style="userPopupStyle" @click.stop>
            <div class="popup-header">
              <img :src="userAvatar" class="popup-avatar" />
              <div class="popup-info">
                <strong>{{ user?.fullName || 'Admin' }}</strong>
                <span>{{ user?.email || 'admin@test.com' }}</span>
              </div>
            </div>
            <div class="popup-menu">
              <router-link to="/app/settings/profile" class="popup-item" @click="showUserMenu = false">
                <vue-feather type="user" size="16" />
                <span>My Profile</span>
              </router-link>
              <router-link to="/app/settings/security" class="popup-item" @click="showUserMenu = false">
                <vue-feather type="shield" size="16" />
                <span>Security</span>
              </router-link>
              <router-link to="/app/settings/app" class="popup-item" @click="showUserMenu = false">
                <vue-feather type="sliders" size="16" />
                <span>Preferences</span>
              </router-link>
              <div class="popup-item popup-theme-toggle" @click="toggleTheme">
                <vue-feather :type="isDarkMode ? 'sun' : 'moon'" size="16" />
                <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
                <div class="theme-switch" :class="{ active: isDarkMode }">
                  <div class="theme-switch-knob"></div>
                </div>
              </div>
            </div>
            <div class="popup-footer">
              <button class="popup-logout" @click="handleLogout">
                <vue-feather type="log-out" size="16" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Search Modal -->
    <Teleport to="body">
      <Transition name="search-modal">
        <div v-if="showSearch" class="search-overlay" @click.self="closeSearch">
          <div class="search-modal">
            <div class="search-header">
              <vue-feather type="search" size="20" />
              <input ref="searchInput" v-model="searchQuery" type="text" 
                placeholder="Search exams, questions, users..." 
                @keydown.esc="closeSearch" @keydown.enter="handleSearch" />
              <kbd>ESC</kbd>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'
import { useAlert } from '@/plugins/sweetalert'
import api from '@/services/api'

const API_BASE = '/v1/admin'

export default {
  name: 'LayoutSidebar',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()
    const { toast } = useNotifications()
    const alert = useAlert()
    const navList = ref(null)
    const searchInput = ref(null)
    const userAvatarBtn = ref(null)
    
    const isAdmin = computed(() => authStore.isAdmin)
    const user = computed(() => authStore.user)
    const pendingApprovals = ref(0)
    const notificationCount = ref(0)
    const isDarkMode = ref(false)
    const showUserMenu = ref(false)
    const showSearch = ref(false)
    const searchQuery = ref('')
    const isMobileOpen = ref(false)
    const userPopupPosition = ref({ top: 0, left: 0 })

    const userAvatar = computed(() => {
      if (user.value?.avatar) return user.value.avatar
      const name = user.value?.fullName || 'A'
      const initial = name.charAt(0).toUpperCase()
      return `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:%23fcbf49"/><stop offset="100%" style="stop-color:%23f77f00"/></linearGradient></defs><circle cx="50" cy="50" r="50" fill="url(%23g)"/><text x="50" y="65" text-anchor="middle" fill="%23003049" font-size="45" font-family="system-ui" font-weight="600">${initial}</text></svg>`
    })

    const userPopupStyle = computed(() => ({
      top: `${userPopupPosition.value.top}px`,
      left: `${userPopupPosition.value.left}px`
    }))

    const isActive = (path) => route.path.startsWith(path)
    const isExamsActive = computed(() => {
      const paths = ['/app/exams', '/app/subjects', '/app/topics', '/app/lessons', '/app/test-series', '/app/tests']
      return paths.some(p => route.path.startsWith(p))
    })

    // Indicator
    const indicatorTop = ref(0)
    const indicatorHeight = ref(0)
    const activeIndex = ref(-1)

    const indicatorStyle = computed(() => ({
      transform: `translateY(${indicatorTop.value}px)`,
      height: `${indicatorHeight.value}px`
    }))

    const updateIndicator = () => {
      nextTick(() => {
        if (!navList.value) return
        const activeItem = navList.value.querySelector('li.active')
        if (activeItem) {
          const listRect = navList.value.getBoundingClientRect()
          const itemRect = activeItem.getBoundingClientRect()
          indicatorTop.value = itemRect.top - listRect.top
          indicatorHeight.value = itemRect.height
          activeIndex.value = 1
        } else {
          activeIndex.value = -1
        }
      })
    }

    const toggleTheme = () => { 
      isDarkMode.value = !isDarkMode.value
      document.body.classList.toggle('dark-mode')
      toast.info(isDarkMode.value ? 'Dark mode enabled' : 'Light mode enabled')
    }

    const toggleUserMenu = () => {
      if (!showUserMenu.value && userAvatarBtn.value) {
        const rect = userAvatarBtn.value.getBoundingClientRect()
        const sidebar = document.querySelector('.sidebar')
        const sidebarWidth = sidebar ? sidebar.offsetWidth : 88
        const popupHeight = 320 // Approximate height with theme toggle
        const popupWidth = 260
        const viewportHeight = window.innerHeight
        const viewportWidth = window.innerWidth
        
        // Calculate top position - ensure popup stays within viewport
        let top = rect.top - popupHeight + rect.height
        if (top < 16) {
          top = 16
        }
        // If popup would go below viewport, position it above the button
        if (top + popupHeight > viewportHeight - 16) {
          top = viewportHeight - popupHeight - 16
        }
        
        // Calculate left position
        let left = sidebarWidth + 8
        // On smaller screens, adjust if popup would overflow
        if (left + popupWidth > viewportWidth - 16) {
          left = viewportWidth - popupWidth - 16
        }
        
        userPopupPosition.value = { top, left }
      }
      showUserMenu.value = !showUserMenu.value
    }

    const openSearch = () => { 
      showSearch.value = true
      nextTick(() => searchInput.value?.focus()) 
    }

    const closeSearch = () => { 
      showSearch.value = false
      searchQuery.value = '' 
    }

    const handleSearch = () => { 
      if (searchQuery.value.trim()) { 
        closeSearch() 
      } 
    }

    const handleLogout = async () => { 
      showUserMenu.value = false
      
      let timerInterval
      const result = await alert.raw.fire({
        title: 'Sign Out?',
        html: 'You will be signed out in <b>5</b> seconds.',
        icon: 'question',
        timer: 5000,
        timerProgressBar: true,
        showCancelButton: true,
        confirmButtonText: 'Sign Out Now',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'swal-popup-custom',
          title: 'swal-title-custom',
          htmlContainer: 'swal-html-custom',
          confirmButton: 'swal-confirm-btn',
          cancelButton: 'swal-cancel-btn',
          actions: 'swal-actions-custom',
          icon: 'swal-icon-custom'
        },
        buttonsStyling: false,
        didOpen: () => {
          const timer = alert.raw.getPopup().querySelector('b')
          timerInterval = setInterval(() => {
            const timeLeft = Math.ceil(alert.raw.getTimerLeft() / 1000)
            timer.textContent = timeLeft
          }, 100)
        },
        willClose: () => {
          clearInterval(timerInterval)
        }
      })

      if (result.isConfirmed || result.dismiss === alert.raw.DismissReason.timer) {
        await authStore.logout()
        toast.success('You have been signed out successfully')
        router.push({ name: 'login' })
      }
    }

    const toggleMobileSidebar = () => { 
      isMobileOpen.value = !isMobileOpen.value 
    }

    const closeMobileSidebar = () => { 
      isMobileOpen.value = false 
    }

    const handleKeydown = (e) => { 
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { 
        e.preventDefault()
        openSearch() 
      }
      if (e.key === 'Escape') {
        showUserMenu.value = false
      }
    }

    const fetchSidebarCounts = async () => {
      try {
        const [approvalsRes, notificationsRes] = await Promise.allSettled([
          api.get(`${API_BASE}/approvals`, { params: { status: 'pending_review' } }),
          api.get(`${API_BASE}/notifications/recent`)
        ])

        if (approvalsRes.status === 'fulfilled') {
          const data = approvalsRes.value.data
          pendingApprovals.value = Array.isArray(data) ? data.length : (data?.data?.length || 0)
        }

        if (notificationsRes.status === 'fulfilled') {
          const data = notificationsRes.value.data
          const notifications = data?.notifications || (Array.isArray(data) ? data : [])
          notificationCount.value = notifications.length
        }
      } catch (err) {
        console.error('Failed to fetch sidebar counts:', err)
      }
    }

    watch(() => route.path, () => { 
      updateIndicator()
      isMobileOpen.value = false
      showUserMenu.value = false 
    })

    onMounted(() => {
      updateIndicator()
      fetchSidebarCounts()
      window.addEventListener('resize', updateIndicator)
      window.addEventListener('keydown', handleKeydown)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', updateIndicator)
      window.removeEventListener('keydown', handleKeydown)
    })

    return { 
      isAdmin, isActive, isExamsActive, pendingApprovals, notificationCount,
      navList, indicatorStyle, activeIndex, user, userAvatar, isDarkMode,
      showUserMenu, showSearch, searchQuery, searchInput, isMobileOpen,
      userAvatarBtn, userPopupStyle,
      toggleTheme, toggleUserMenu, openSearch, closeSearch, handleSearch,
      handleLogout, toggleMobileSidebar, closeMobileSidebar
    }
  }
}
</script>

<style scoped>
/* Mobile Menu Button */
.mobile-menu-btn {
  display: none;
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 1001;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: var(--primary-color, #fcbf49);
  color: var(--dark-color, #003049);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s, box-shadow 0.2s;
}

.mobile-menu-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

/* Sidebar Overlay */
.sidebar-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sidebar-overlay.active {
  opacity: 1;
}

/* Sidebar */
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: clamp(72px, 6vw, 88px);
  background: linear-gradient(180deg, #003049 0%, #002438 100%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px 0;
}

/* Logo */
.sidebar-logo {
  padding: 8px 0 16px;
  display: flex;
  justify-content: center;
}

.logo-link {
  text-decoration: none;
}

.logo-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, box-shadow 0.2s;
  overflow: hidden;
}

.logo-icon-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 16px rgba(0, 48, 73, 0.3);
}

.logo-icon-wrapper .logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0;
  position: relative;
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-list li {
  position: relative;
}

.nav-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 12px 16px !important;
}

.nav-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.2s, background 0.2s;
  border-radius: 8px;
  margin: 0 8px;
  gap: 6px;
}

.nav-link:hover {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.05);
}

li.active .nav-link {
  color: #fcbf49;
  background: rgba(252, 191, 73, 0.1);
}

.nav-icon, .nav-icon-badge {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-text {
  font-size: clamp(9px, 1.2vw, 11px);
  font-weight: 500;
  text-align: center;
  line-height: 1.2;
  word-wrap: break-word;
  max-width: 100%;
}

/* Badge */
.badge {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 600;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Active Indicator */
.active-indicator {
  position: absolute;
  left: 0;
  width: 3px;
  background: linear-gradient(180deg, #fcbf49 0%, #f77f00 100%);
  border-radius: 0 3px 3px 0;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

/* Bottom Section */
.sidebar-bottom {
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
}

.bottom-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.bottom-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fcbf49;
  transform: scale(1.05);
}

.user-avatar-btn {
  position: relative;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  background: transparent;
  transition: transform 0.2s, box-shadow 0.2s;
}

.user-avatar-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 0 0 3px rgba(252, 191, 73, 0.3);
}

.user-avatar-btn img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.online-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 10px;
  height: 10px;
  background: #22c55e;
  border: 2px solid #003049;
  border-radius: 50%;
}

/* User Popup Overlay */
.user-popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
}

/* User Popup */
.user-popup {
  position: fixed;
  width: 260px;
  max-height: calc(100vh - 32px);
  background: white;
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  z-index: 2001;
  transform-origin: left bottom;
  display: flex;
  flex-direction: column;
}

.popup-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: linear-gradient(135deg, #003049 0%, #002438 100%);
  color: white;
  flex-shrink: 0;
}

.popup-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(252, 191, 73, 0.5);
  flex-shrink: 0;
}

.popup-info {
  flex: 1;
  min-width: 0;
}

.popup-info strong {
  display: block;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.popup-info span {
  display: block;
  font-size: 12px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.popup-menu {
  padding: 6px;
  overflow-y: auto;
  flex: 1;
}

.popup-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  color: #374151;
  font-size: 14px;
  transition: background 0.15s ease;
  cursor: pointer;
}

.popup-item:hover {
  background: #f3f4f6;
}

.popup-item span {
  flex: 1;
}

.popup-theme-toggle {
  border-top: 1px solid #f3f4f6;
  margin-top: 4px;
  padding-top: 14px;
}

.theme-switch {
  width: 36px;
  height: 20px;
  background: #e5e7eb;
  border-radius: 10px;
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.theme-switch.active {
  background: #003049;
}

.theme-switch-knob {
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.theme-switch.active .theme-switch-knob {
  transform: translateX(16px);
}

.popup-footer {
  padding: 6px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.popup-logout {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #ef4444;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.popup-logout:hover {
  background: #fef2f2;
}

/* User Popup Animation - Smooth scale and fade */
.user-popup-enter-active {
  transition: opacity 0.15s ease-out;
}

.user-popup-enter-active .user-popup {
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.15s ease-out;
}

.user-popup-leave-active {
  transition: opacity 0.12s ease-in;
}

.user-popup-leave-active .user-popup {
  transition: transform 0.12s ease-in, opacity 0.12s ease-in;
}

.user-popup-enter-from {
  opacity: 0;
}

.user-popup-enter-from .user-popup {
  opacity: 0;
  transform: scale(0.9) translateY(8px);
}

.user-popup-leave-to {
  opacity: 0;
}

.user-popup-leave-to .user-popup {
  opacity: 0;
  transform: scale(0.95) translateY(4px);
}

/* Search Overlay */
.search-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
}

.search-modal {
  width: 100%;
  max-width: 560px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.search-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.search-header svg {
  color: #9ca3af;
  flex-shrink: 0;
}

.search-header input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  background: transparent;
}

.search-header input::placeholder {
  color: #9ca3af;
}

.search-header kbd {
  padding: 4px 8px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 12px;
  color: #6b7280;
  font-family: inherit;
}

/* Search Modal Animation */
.search-modal-enter-active,
.search-modal-leave-active {
  transition: opacity 0.2s ease;
}

.search-modal-enter-active .search-modal,
.search-modal-leave-active .search-modal {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.search-modal-enter-from,
.search-modal-leave-to {
  opacity: 0;
}

.search-modal-enter-from .search-modal,
.search-modal-leave-to .search-modal {
  transform: translateY(-20px) scale(0.95);
  opacity: 0;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .mobile-menu-btn {
    display: flex;
  }

  .sidebar-overlay {
    display: block;
    pointer-events: none;
  }

  .sidebar-overlay.active {
    pointer-events: auto;
  }

  .sidebar {
    width: 260px;
    transform: translateX(-100%);
  }

  .sidebar.mobile-open {
    transform: translateX(0);
  }

  .nav-link {
    flex-direction: row;
    justify-content: flex-start;
    padding: 14px 16px;
    gap: 12px;
  }

  .nav-text {
    font-size: 14px;
  }

  .sidebar-bottom {
    flex-direction: row;
    justify-content: center;
    padding: 16px;
  }

  .user-popup {
    position: fixed !important;
    left: 16px !important;
    right: 16px !important;
    bottom: 16px !important;
    top: auto !important;
    width: auto !important;
    max-height: 70vh;
    transform-origin: center bottom;
  }
}

@media (max-height: 500px) {
  .user-popup {
    max-height: calc(100vh - 100px);
  }
  
  .popup-menu {
    max-height: 200px;
  }
}

/* Dark Mode */
:global(body.dark-mode) .user-popup {
  background: #1f2937;
}

:global(body.dark-mode) .popup-item {
  color: #e5e7eb;
}

:global(body.dark-mode) .popup-item:hover {
  background: #374151;
}

:global(body.dark-mode) .popup-theme-toggle {
  border-color: #374151;
}

:global(body.dark-mode) .theme-switch {
  background: #4b5563;
}

:global(body.dark-mode) .popup-footer {
  border-color: #374151;
}

:global(body.dark-mode) .popup-logout:hover {
  background: rgba(239, 68, 68, 0.1);
}

:global(body.dark-mode) .search-modal {
  background: #1f2937;
}

:global(body.dark-mode) .search-header {
  border-color: #374151;
}

:global(body.dark-mode) .search-header input {
  color: white;
}

:global(body.dark-mode) .search-header kbd {
  background: #374151;
  color: #9ca3af;
}
</style>
